import { get, flatten, uniq } from 'lodash';
import {compose, curriedMap, curriedReduce, curriedDefaultTo, curriedJoin, prop} from '@core/lib/fp';
import errorHandler from '@core/lib/errorHandler/errorHandler';
import * as NotificationsApi from '@core/api/notifications';

function Notification($q, $state, gettext) {
  'ngInject';

  /**
   * @param {Object} notification
   * @param {Number} accountId
   * @returns {Promise}
   */
  function dismissNotification(notification, accountId) {
    const method = notification.accountType
      ? NotificationsApi.dismissAccountNotification(notification.id, accountId)
      : NotificationsApi.dismissUserNotification(notification.id);

    return $q.when(method);
  }

  /**
   * Get total notifications count (which is composed with: userNotificationsCount + accountNotificationsCount)
   *
   * @param {Object}  [options]
   * @param {Boolean} [options.forceUpdate=false]
   * @returns {Promise}
   */
  function getNotificationsCount({ forceUpdate = false } = {}) {
    return $q.when(NotificationsApi.getNotificationsCount({ forceUpdate }))
      .catch(() => 0);
  }

  /**
   * Get combined list of account and user notifications
   *
   * @param {Object}  params
   * @param {Number}  params.accountId
   * @param {Number}  [params.offset=0]
   * @param {Object}  [options]
   * @param {Boolean} [options.forceUpdate=false]
   * @returns {Promise<Array>}
   */
  function getNotifications({ accountId, offset = 0 }, { forceUpdate = false } = {}) {
    return $q
      .all([
        _getUserNotifications({ offset }, { forceUpdate }),
        _getAccountNotifications({ accountId, offset }, { forceUpdate })
      ])
      .then(flatten);
  }

  /**
   * Safely fetches the list of notifications for the current user without propagating errors
   * In case of failure an empty array is resolved
   *
   * @param {Object}  params
   * @param {Object}  [options]
   * @returns {Promise<Array>}
   * @private
   */
  function _getUserNotifications(params, options) {
    return $q.when(NotificationsApi.getUserNotifications(params, options))
      .then(_prepareUserMessages)
      .catch(() => []);
  }

  /**
   * @param {Array} notifications
   * @returns {Array}
   * @private
   */
  const _prepareUserMessages = compose(
    curriedMap(_createUserMessage),
    curriedDefaultTo([])
  );

  /**
   * @param {Object} notification
   * @returns {Object}
   * @private
   */
  function _createUserMessage(notification) {
    return {
      ...notification,
      accountType: false,
      message: {
        body: notification.message
      }
    };
  }

  /**
   * Safely fetches the list of notifications for the current account without propagating errors
   * In case of failure an empty array is resolved
   *
   * @param {Object}  params
   * @param {Object}  [options]
   * @returns {Promise<Array>}
   * @private
   */
  function _getAccountNotifications(params, options) {
    return $q.when(NotificationsApi.getAccountNotifications(params, options))
      .then(_prepareAccountMessages)
      .then(_flattenAccountMessages)
      .catch(() => []);
  }

  /**
   * Prepares the list of processed account messages while also triggering
   * the report for unhandled notifications
   *
   * @param {Array} notifications
   * @returns {[Array, Array]}
   * @private
   */
  const _prepareAccountMessages = compose(
    curriedReduce(_accountMessagesReducer, [[], []]),
    curriedDefaultTo([])
  );

  /**
   * @param {[Array, Array]} collections
   * @param {Object} notification
   * @returns {[Array, Array]}
   * @private
   */
  function _accountMessagesReducer([handled, unhandled], notification) {
    switch (notification.message) {
    case 'dashboard_csv_upload_upload':
      return [
        [...handled, _createCSVUploadGenericMessage(notification)],
        unhandled
      ];
    case 'dashboard_uninstall_detector_detection':
      return [
        [...handled, _createUninstallMessage(notification)],
        unhandled
      ];
    case 'dashboard_partner_error':
      return [
        [...handled, _createAudienceBuilderMessage(notification)],
        unhandled
      ];
      case 'dashboard_subscription_invalid_credentials':
        return  [
          [...handled, _createSubscriptionCredentialsInvalidMessage(notification)],
          unhandled,
        ];
    default:
      return [
        handled,
        [...unhandled, notification]
      ];
    }
  }

  /**
   * @param {Object} notification
   * @returns {Object}
   * @private
   */
  function _createCSVUploadGenericMessage(notification) {
    return {
      ...notification,
      accountType: true,
      message: {
        title: t('account-notifications.csv-upload.title'),
        body: t('account-notifications.csv-upload.upload-error', {
          appToken: notification.data.apptoken,
          appName: notification.data.appname
        }),
        errorReference: notification.data.error_message,
        action: t('account-notifications.csv-upload.failed.call-for-action')
      }
    };
  }

  /**
   * @param {Object} notification
   * @returns {Object}
   * @private
   */
  function _createAudienceBuilderMessage(notification) {
    const notificationUrl = $state.href('segmentation.view-segment', { id: notification.data.segment_id });
    const segmentName = `<a href="${notificationUrl}">${notification.data.segment_name}</a>`;
    const partnerName = t(notification.data.partner);

    return {
      ...notification,
      accountType: true,
      message: {
        title: t('account-notifications.group-error-header', {
          partnerName
        }),
        body: t('account-notifications.group-error-header-description', {
          segmentName,
        }),
        segmentName,
        partnerName
      }
    };
  }

  /**
   * @param {Object} notification
   * @returns {Object}
   * @private
   */
  function _createSubscriptionCredentialsInvalidMessage(notification) {
    const { appname, apptoken, platform, error_message } = notification.data;

    return {
      ...notification,
      accountType: true,
      message: {
        title: t('account-notifications.subscription-credentials.title'),
        body: t('account-notifications.subscription-credentials.body', {
          appName: appname,
          appToken: apptoken,
          platform: platform,
        }),
        errorReference: error_message,
      }
    };
  }

  /**
   * @param {Object} notification
   * @returns {Object}
   * @private
   */
  function _createUninstallMessage(notification) {
    const errorType = _parseUninstallErrorType(notification);
    const errorMessagesMap = {
      'credentials are invalid': gettext('account-notifications.uninstall.invalid-credentials-error'),
      'credentials are expired': gettext('account-notifications.uninstall.expired-credentials-error'),
      'all devices failed - ios': gettext('account-notifications.uninstall.all-devices-failed-ios-error'),
      'all devices failed - google': gettext('account-notifications.uninstall.all-devices-failed-google-error')
    };

    return errorMessagesMap[errorType]
      ? _createUninstallSpecializedMessage(errorMessagesMap[errorType], notification)
      : _createUninstallGenericMessage(notification);
  }

  /**
   * @param {Object} notification
   * @returns {Object}
   * @private
   */
  function _createUninstallGenericMessage(notification) {
    return {
      ...notification,
      accountType: true,
      message: {
        title: t('account-notifications.uninstall.title'),
        body: t('account-notifications.uninstall.generic-error', {
          appToken: notification.data.apptoken,
          appName: notification.data.appname,
          platform: _mapUninstallPlatformName(notification.data.platform)
        }),
        errorReference: notification.data.error_message
      }
    };
  }

  /**
   * @param {String} platform
   * @returns {String}
   * @private
   */
  function _mapUninstallPlatformName(platform) {
    const platforms = {
      'ios': t('uninstall.platform.itunes'),
      'android': t('uninstall.platform.google')
    };

    return platforms[platform] || platform;
  }

  /**
   * @param key {String}
   * @param {Object} notification
   * @returns {Object}
   * @private
   */
  function _createUninstallSpecializedMessage(key, notification) {
    return {
      ...notification,
      accountType: true,
      message: {
        title: t('account-notifications.uninstall.title'),
        body: t(key, {
          appToken: notification.data.apptoken,
          appName: notification.data.appname,
          platform: _mapUninstallPlatformName(notification.data.platform)
        })
      }
    };
  }

  /**
   * Handles special cases that lead to further branching and flattens the cases
   * into a single list of posibilities.
   *
   * @param {Object} notification
   * @returns {String}
   * @private
   */
  function _parseUninstallErrorType(notification) {
    const errorType = get(notification, 'data.error_message');

    return errorType === 'all devices failed'
      ? `${errorType} - ${notification.data.platform}`
      : errorType;
  }

  /**
   * Flattens the account messages piles into a single array containing only the
   * handled messages while triggering the unhandler error report
   *
   * @param {[Array, Array]} accountMessages
   * @returns {Array}
   * @private
   */
  const _flattenAccountMessages = ([handled, unhandled]) => {
    _reportUnhandledNotifications(unhandled);
    return handled;
  };

  /**
   * @param {Array} unhandled
   * @private
   */
  function _reportUnhandledNotifications(unhandled) {
    if (!unhandled.length) {
      return;
    }

    const errMsg = _createUnhandledErrorMsg(unhandled);
    errorHandler.trackError(new Error(errMsg));
  }

  /**
   * @param {Array} unhandled
   * @returns {String}
   * @private
   */
  const _createUnhandledErrorMsg = compose(
    str => `Unhandled notification type: ${str}`,
    curriedJoin(', '),
    uniq,
    curriedMap(prop('message'))
  );

  return {
    dismissNotification,
    getNotifications,
    getNotificationsCount,
    _getUserNotifications,
    _prepareUserMessages,
    _createUserMessage,
    _getAccountNotifications,
    _prepareAccountMessages,
    _accountMessagesReducer,
    _createCSVUploadGenericMessage,
    _createAudienceBuilderMessage,
    _createUninstallMessage,
    _parseUninstallErrorType,
    _createUninstallSpecializedMessage,
    _mapUninstallPlatformName,
    _createUninstallGenericMessage,
    _flattenAccountMessages,
    _reportUnhandledNotifications,
    _createUnhandledErrorMsg,
    _createSubscriptionCredentialsInvalidMessage,
  };
}

export { Notification };
